/**
 * PrimaryBtn Component
 *
 * Description: This component renders a customizable button with an optional loading spinner.
 * It supports dynamic button text, a loading state, click event handling, and additional custom styles.
 *
 * Props:
 * - ButtonText (string): Text to display on the button (default: "").
 * - loader (boolean): Whether to show a loading spinner (default: false).
 * - onPress (function): Function to call when the button is clicked (default: empty function).
 * - type (string): The type of the button, e.g., "button", "submit" (default: "").
 * - btnExtraStyle (object): Additional CSS styles to apply to the button (default: empty object).
 * - extraProps (object): Additional props to be applied to the button element (default: empty object).
 *
 * File Name: PrimaryBtn.jsx
 * Date: 23-07-2024
 */

import React from "react";
import { CircularProgress } from "@mui/material";
import { convertToKebabCase } from "src/helpers/helperFunctions";

interface IPrimaryBtnProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  ButtonText: string;
  loader?: boolean;
  loaderColorGreen?: boolean;
  onPress?: () => void;
  type?: "button" | "submit" | "reset"; // Type adjusted to possible button types
  btnExtraStyle?: React.CSSProperties; // Correct type for CSS styles;
  btnExtralClasses?: string;
  id?: string;
  fontFamily?: string;
  fontSize?: string;
  count?: number;
  countShown?: boolean;
  disabledOpactiy?: boolean;
}

const PrimaryBtn: React.FC<IPrimaryBtnProps> = ({
  ButtonText = "",
  loader = false,
  onPress = () => {},
  type = "button",
  btnExtraStyle = {},
  btnExtralClasses = "",
  loaderColorGreen = false,
  id = "",
  fontFamily = "font-Poppins-SemiBold",
  fontSize = "font-size-16px",
  count = 0,
  countShown = false,
  disabledOpactiy = false,
  ...extraProps
}) => {
  // Style for the loading spinner
  const loaderStyle = {
    width: 14,
    height: 14,
    color: `${loaderColorGreen ? "#004641" : "#FFFFFF"}`,
  };
  return (
    <button
      className={`theme-bg-light-seagreen ${disabledOpactiy ? `disabled:opacity-[0.5]` : ""} relative text-[#FFFFFF] ${fontFamily} ${fontSize} flex items-center justify-center gap-2 primary-btn-admin ${btnExtralClasses}`}
      disabled={loader}
      onClick={onPress}
      type={type}
      style={{ ...btnExtraStyle }}
      id={id || convertToKebabCase(ButtonText || "")}
      {...extraProps} // Apply additional props
    >
      {ButtonText}
      {countShown && count > 0 && (
        <span className="font-size-10px w-[15px] h-[15px] absolute top-[-5px] right-[-5px] lg:w-[1vw] lg:h-[1vw] flex items-center justify-center font-Poppins-SemiBold bg-red-500 text-white rounded-full">
          {count}
        </span>
      )}

      {loader && <CircularProgress style={loaderStyle} />}
    </button>
  );
};

export default PrimaryBtn;
